import { useState, useEffect, useCallback } from 'react';
import { Search, RefreshCw, ChevronLeft, ChevronRight, X } from 'lucide-react';
import api from '../services/api';

function Badge({ status }) {
  const map = {
    synced:   { bg: '#e8f5e9', color: '#2e7d32', label: '✓ Cegid' },
    pending:  { bg: '#fff8e1', color: '#f57c00', label: '⏳ Pendiente' },
    error:    { bg: '#ffebee', color: '#c62828', label: '✗ Error' },
    active:   { bg: '#e3f2fd', color: '#1565c0', label: 'Activo' },
    inactive: { bg: '#f5f5f5', color: '#757575', label: 'Inactivo' },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{ background: s.bg, color: s.color, padding: '2px 10px', borderRadius: '99px', fontSize: '12px', fontWeight: 600 }}>
      {s.label}
    </span>
  );
}

function Modal({ customer, onClose, onRetrySync, onSyncPoints, syncingPoints }) {
  if (!customer) return null;
  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={{ margin: 0, fontSize: '17px' }}>Detalle del cliente</h2>
          <button onClick={onClose} style={styles.closeBtn}><X size={18} /></button>
        </div>
        <div style={styles.modalBody}>
          {[
            ['Nombre', `${customer.first_name} ${customer.last_name}`],
            ['Email', customer.email],
            ['Teléfono', customer.phone || '—'],
            ['Puntos', `${customer.points_balance} pts`],
            ['Nivel', customer.tier],
            ['Estado Cegid', <Badge status={customer.cegid_sync_status} />],
            ['ID Cegid', customer.cegid_customer_id || 'Pendiente'],
            ['Última sync', customer.cegid_synced_at ? new Date(customer.cegid_synced_at).toLocaleString('es-MX') : '—'],
            ['Registrado', new Date(customer.created_at).toLocaleString('es-MX')],
            ['Marketing', customer.accepts_marketing ? '✅ Sí' : '❌ No'],
          ].map(([label, value]) => (
            <div key={label} style={styles.detailRow}>
              <span style={styles.detailLabel}>{label}</span>
              <span style={styles.detailValue}>{value}</span>
            </div>
          ))}
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
            {customer.cegid_sync_status !== 'synced' && (
              <button onClick={() => onRetrySync(customer.id)} style={styles.btnPrimary}>
                🔄 Reintentar sync Cegid
              </button>
            )}
            {customer.cegid_customer_id && (
              <button
                onClick={() => onSyncPoints(customer.id)}
                disabled={syncingPoints}
                style={{ ...styles.btnPrimary, opacity: syncingPoints ? 0.6 : 1, cursor: syncingPoints ? 'wait' : 'pointer' }}
              >
                {syncingPoints ? '⏳ Sincronizando...' : '⬇️ Pull puntos desde Cegid'}
              </button>
            )}
            <button onClick={onClose} style={styles.btnSecondary}>Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Clientes() {
  const [customers, setCustomers]   = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [search, setSearch]         = useState('');
  const [syncFilter, setSyncFilter] = useState('');
  const [selected, setSelected]     = useState(null);
  const [loading, setLoading]       = useState(true);
  const [syncingPoints, setSyncingPoints] = useState(false);
  const [bulkSyncing, setBulkSyncing]     = useState(false);

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20, ...(search && { search }), ...(syncFilter && { cegid_sync_status: syncFilter }) };
      const res = await api.get('/api/customers', { params });
      setCustomers(res.data.data);
      setPagination(res.data.pagination);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, syncFilter]);

  useEffect(() => { load(1); }, [load]);

  const retrySync = async (id) => {
    await api.post(`/api/customers/${id}/sync-cegid`);
    load(pagination.page);
    setSelected(null);
  };

  // Pull de puntos desde Cegid para UN cliente
  const syncPointsOne = async (id) => {
    setSyncingPoints(true);
    try {
      const res = await api.post(`/api/admin/sync-points/${id}`);
      const data = res.data?.data;
      if (data?.success) {
        if (data.changed) {
          alert(`✅ Puntos actualizados\n\nDe ${data.oldBalance} → ${data.newBalance} pts`);
        } else {
          alert(`ℹ️ Sin cambios\n\nBalance actual: ${data.newBalance} pts`);
        }
      } else {
        alert(`❌ ${data?.message || 'No se pudo sincronizar'}`);
      }
      load(pagination.page);
      // Refrescar el cliente seleccionado en el modal si está abierto
      if (selected?.id === id) {
        const refreshed = await api.get(`/api/customers/${id}`);
        if (refreshed.data?.data) setSelected(refreshed.data.data);
      }
    } catch (e) {
      alert(`Error: ${e.response?.data?.message || e.message}`);
    } finally {
      setSyncingPoints(false);
    }
  };

  // Pull de puntos masivo (todos los clientes con cegid_customer_id)
  const syncPointsAll = async () => {
    if (!window.confirm('¿Sincronizar puntos de TODOS los clientes desde Cegid?\n\nPuede tardar unos segundos según la cantidad de clientes.')) return;
    setBulkSyncing(true);
    try {
      const res = await api.post('/api/admin/sync-points-now');
      const stats = res.data?.stats;
      if (stats) {
        alert(`✅ Sync masivo completado\n\nTotal:    ${stats.total}\nOK:       ${stats.ok}\nCambios:  ${stats.changed}\nErrores:  ${stats.errors}`);
      } else {
        alert('Sync ejecutado, ver respuesta del servidor.');
      }
      load(pagination.page);
    } catch (e) {
      alert(`Error: ${e.response?.data?.message || e.message}`);
    } finally {
      setBulkSyncing(false);
    }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Clientes</h1>
          <p style={styles.subtitle}>{pagination.total} clientes registrados</p>
        </div>
        <button
          onClick={syncPointsAll}
          disabled={bulkSyncing}
          style={{ ...styles.btnPrimary, opacity: bulkSyncing ? 0.6 : 1, cursor: bulkSyncing ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          {bulkSyncing ? '⏳ Sincronizando...' : '⬇️ Pull puntos (todos)'}
        </button>
      </div>

      <div style={styles.toolbar}>
        <div style={styles.searchWrap}>
          <Search size={16} style={styles.searchIcon} />
          <input
            style={styles.searchInput}
            placeholder="Buscar por nombre, email o teléfono..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select style={styles.select} value={syncFilter} onChange={(e) => setSyncFilter(e.target.value)}>
          <option value="">Todo Cegid</option>
          <option value="synced">Sincronizados</option>
          <option value="pending">Pendientes</option>
          <option value="error">Con error</option>
        </select>
        <button onClick={() => load(pagination.page)} style={styles.btnIcon} title="Actualizar">
          <RefreshCw size={16} />
        </button>
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {['Nombre', 'Email', 'Teléfono', 'Puntos', 'Cegid', 'Registro', 'Acciones'].map((h) => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={styles.empty}>Cargando...</td></tr>
            ) : customers.length === 0 ? (
              <tr><td colSpan={7} style={styles.empty}>Sin resultados</td></tr>
            ) : customers.map((c) => (
              <tr key={c.id} style={styles.tr}>
                <td style={styles.td}><strong>{c.first_name} {c.last_name}</strong></td>
                <td style={styles.td}>{c.email}</td>
                <td style={styles.td}>{c.phone || '—'}</td>
                <td style={styles.td}><strong>{c.points_balance}</strong> pts</td>
                <td style={styles.td}><Badge status={c.cegid_sync_status} /></td>
                <td style={styles.td}>{fmt(c.created_at)}</td>
                <td style={styles.td}>
                  <button onClick={() => setSelected(c)} style={styles.btnAction}>Ver</button>
                  {c.cegid_sync_status === 'error' && (
                    <button onClick={() => retrySync(c.id)} style={{ ...styles.btnAction, background: '#ffebee', color: '#c62828', marginLeft: '4px' }}>Retry</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={styles.pagination}>
        <button disabled={pagination.page <= 1} onClick={() => load(pagination.page - 1)} style={styles.pageBtn}><ChevronLeft size={16} /></button>
        <span style={{ fontSize: '13px', color: '#666' }}>Página {pagination.page} de {pagination.totalPages}</span>
        <button disabled={pagination.page >= pagination.totalPages} onClick={() => load(pagination.page + 1)} style={styles.pageBtn}><ChevronRight size={16} /></button>
      </div>

      <Modal
        customer={selected}
        onClose={() => setSelected(null)}
        onRetrySync={retrySync}
        onSyncPoints={syncPointsOne}
        syncingPoints={syncingPoints}
      />
    </div>
  );
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '24px', fontWeight: 700, margin: '0 0 4px', color: '#1a1a1a' },
  subtitle: { fontSize: '14px', color: '#888', margin: 0 },
  toolbar: { display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' },
  searchWrap: { position: 'relative', flex: 1, minWidth: '200px' },
  searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#aaa' },
  searchInput: { width: '100%', padding: '9px 14px 9px 36px', border: '1.5px solid #ddd', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  select: { padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '8px', fontSize: '13px', background: '#fff', cursor: 'pointer', outline: 'none' },
  btnIcon: { padding: '9px 12px', border: '1.5px solid #ddd', borderRadius: '8px', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  tableWrap: { background: '#fff', borderRadius: '12px', border: '1px solid #eee', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { background: '#f8f9fa', textAlign: 'left', padding: '12px 16px', fontWeight: 600, color: '#888', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #eee' },
  tr: { borderBottom: '1px solid #f0f0f0', transition: 'background 0.1s' },
  td: { padding: '12px 16px', verticalAlign: 'middle' },
  empty: { textAlign: 'center', padding: '40px', color: '#aaa' },
  btnAction: { background: '#e3f2fd', color: '#1565c0', border: 'none', borderRadius: '6px', padding: '5px 10px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' },
  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', padding: '20px 0 4px' },
  pageBtn: { background: '#fff', border: '1.5px solid #ddd', borderRadius: '7px', padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', zIndex: 200 },
  modalCard: { background: '#fff', borderRadius: '12px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', width: '100%', maxWidth: '500px', maxHeight: '85vh', overflowY: 'auto' },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #eee', position: 'sticky', top: 0, background: '#fff' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#888', padding: '4px', display: 'flex' },
  modalBody: { padding: '24px' },
  detailRow: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f0f0f0', fontSize: '14px' },
  detailLabel: { color: '#888' },
  detailValue: { fontWeight: 500, textAlign: 'right', maxWidth: '60%' },
  btnPrimary: { background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '7px', padding: '9px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' },
  btnSecondary: { background: '#f5f5f5', color: '#333', border: 'none', borderRadius: '7px', padding: '9px 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' },
};
